import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAppStore } from '../../store/appStore';
import { useI18n } from '../../i18n';
import { REGIONS, CATEGORIES } from '../../data/mockData';
import { PropertyCategory, AdType, HeatingType, PropertyFeatures, LocationFilter, SavedSearch, Property, Auction } from '../../types';
import { MultiLocationSearch } from '../common/MultiLocationSearch';
import { Save, FolderOpen, Trash2 } from 'lucide-react';

// Demand type for location search
interface DemandLocation {
  id: string;
  location: {
    region: string;
    city: string;
    address?: string;
    lat?: number;
    lng?: number;
  };
}

// Konfiguracija filtera po kategoriji
// Definiše koji filteri su vidljivi za svaku kategoriju
interface CategoryFilterConfig {
  showRooms: boolean;
  showHeating: boolean;
  showSquareMeters: boolean;
  showFloor: boolean;
  showPricePerUnit: boolean;
  // Karakteristike specifične za kategoriju
  features: string[];
}

const CATEGORY_FILTER_CONFIG: Record<string, CategoryFilterConfig> = {
  'Kuća': {
    showRooms: true,
    showHeating: true,
    showSquareMeters: true,
    showFloor: false,
    showPricePerUnit: true,
    features: ['odmahUseljiv', 'uknjizen', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor', 'lift']
  },
  'Stan': {
    showRooms: true,
    showHeating: true,
    showSquareMeters: true,
    showFloor: true,
    showPricePerUnit: true,
    features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor']
  },
  'Poslovni prostor': {
    showRooms: true,
    showHeating: false,
    showSquareMeters: true,
    showFloor: true,
    showPricePerUnit: true,
    features: ['lift', 'klima', 'internet', 'garaza', 'parkingMesto', 'videoNadzor']
  },
  'Plac': {
    showRooms: false,
    showHeating: false,
    showSquareMeters: false, // Koristi površinu placa umesto kvadrature
    showFloor: false,
    showPricePerUnit: true, // EUR/ar
    features: ['uknjizen']
  },
  'Garaža': {
    showRooms: false,
    showHeating: false,
    showSquareMeters: false,
    showFloor: false,
    showPricePerUnit: false,
    features: ['uknjizen', 'videoNadzor']
  }
};

// Funkcija za dobijanje konfiguracije filtera za kategoriju
const getCategoryConfig = (category: string | undefined): CategoryFilterConfig => {
  if (!category) {
    // Ako nije izabrana kategorija, prikaži sve filtere
    return {
      showRooms: true,
      showHeating: true,
      showSquareMeters: true,
      showFloor: true,
      showPricePerUnit: true,
      features: ['odmahUseljiv', 'uknjizen', 'lift', 'terasa', 'garaza', 'parkingMesto', 'klima', 'internet', 'videoNadzor']
    };
  }
  return CATEGORY_FILTER_CONFIG[category] || CATEGORY_FILTER_CONFIG['Stan'];
};

interface FilterSidebarProps {
  showAdType?: boolean;
  showPricePerUnit?: boolean; // Show price per m²/ar filter (for Ponude/Potražnje/Aukcije pages)
  onClose?: () => void;
  /** Active listings to include their addresses in location search */
  listings?: (Property | Auction | DemandLocation)[];
  /** Page type for context */
  pageType?: 'offers' | 'demands' | 'auctions';
}

export function FilterSidebar({ showAdType = true, showPricePerUnit = false, onClose, listings = [], pageType }: FilterSidebarProps) {
  const { filters, setFilters, clearFilters, isAuthenticated, user, savedSearches, saveSearch, loadSavedSearches, deleteSavedSearch } = useAppStore();
  const { language, t } = useI18n();
  const [expandedSections, setExpandedSections] = useState<string[]>(['location', 'type', 'price']);
  const [priceUnitType, setPriceUnitType] = useState<'m2' | 'ar'>('m2');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [showSavedSearches, setShowSavedSearches] = useState(false);

  const selectedRegion = REGIONS.find(r => r.name === filters.region);
  
  // Dobij konfiguraciju filtera za izabranu kategoriju
  const categoryConfig = getCategoryConfig(filters.category);
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const heatingOptions: { value: HeatingType; label: { sr: string; en: string } }[] = [
    { value: 'CG', label: { sr: 'Centralno grejanje', en: 'Central heating' } },
    { value: 'EG', label: { sr: 'Etažno grejanje', en: 'Floor heating' } },
    { value: 'TA', label: { sr: 'TA peć', en: 'Storage heater' } },
    { value: 'Gas', label: { sr: 'Gas', en: 'Gas' } },
    { value: 'Drvo', label: { sr: 'Drvo', en: 'Wood' } },
    { value: 'Bez grejanja', label: { sr: 'Bez grejanja', en: 'No heating' } },
  ];

  const featureOptions: { key: keyof PropertyFeatures; label: { sr: string; en: string } }[] = [
    { key: 'odmahUseljiv', label: { sr: 'Odmah useljiv', en: 'Move-in ready' } },
    { key: 'uknjizen', label: { sr: 'Uknjižen', en: 'Registered' } },
    { key: 'lift', label: { sr: 'Lift', en: 'Elevator' } },
    { key: 'terasa', label: { sr: 'Terasa', en: 'Terrace' } },
    { key: 'garaza', label: { sr: 'Garaža', en: 'Garage' } },
    { key: 'parkingMesto', label: { sr: 'Parking mesto', en: 'Parking spot' } },
    { key: 'klima', label: { sr: 'Klima', en: 'Air conditioning' } },
    { key: 'internet', label: { sr: 'Internet', en: 'Internet' } },
    { key: 'videoNadzor', label: { sr: 'Video nadzor', en: 'Video surveillance' } },
  ];

  // Filtrirane karakteristike na osnovu kategorije
  const filteredFeatureOptions = featureOptions.filter(opt => 
    categoryConfig.features.includes(opt.key)
  );

  const SectionHeader = ({ title, section }: { title: string; section: string }) => (
    <button
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full py-2 text-left font-semibold text-slate-900 dark:text-white"
    >
      {title}
      <svg
        className={`w-5 h-5 transition-transform ${expandedSections.includes(section) ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-4 transition-colors"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t.filters.title}</h2>
        <button
          onClick={clearFilters}
          className="text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          {t.filters.clearFilters}
        </button>
      </div>

      {/* Saved Searches - Only for authenticated users */}
      {isAuthenticated && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-sm"
            >
              <Save className="w-4 h-4" />
              {language === 'sr' ? 'Sačuvaj' : 'Save'}
            </button>
            <button
              onClick={() => {
                loadSavedSearches();
                setShowSavedSearches(!showSavedSearches);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-sm"
            >
              <FolderOpen className="w-4 h-4" />
              {language === 'sr' ? 'Učitaj' : 'Load'}
            </button>
          </div>

          {/* Save Search Modal */}
          {showSaveModal && (
            <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder={language === 'sr' ? 'Ime pretrage...' : 'Search name...'}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 mb-2"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (searchName.trim()) {
                      saveSearch(searchName.trim(), pageType);
                      setSearchName('');
                      setShowSaveModal(false);
                    }
                  }}
                  disabled={!searchName.trim()}
                  className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {language === 'sr' ? 'Sačuvaj' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setSearchName('');
                    setShowSaveModal(false);
                  }}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg text-sm hover:bg-slate-300 dark:hover:bg-slate-500"
                >
                  {language === 'sr' ? 'Otkaži' : 'Cancel'}
                </button>
              </div>
            </div>
          )}

          {/* Saved Searches List - filtered by pageType */}
          {showSavedSearches && (() => {
            const filteredSearches = savedSearches.filter(s => !s.pageType || s.pageType === pageType);
            return filteredSearches.length > 0 ? (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {filteredSearches.map((search) => (
                  <div
                    key={search.id}
                    className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                  >
                    <button
                      onClick={() => {
                        setFilters(search.filters);
                        setShowSavedSearches(false);
                      }}
                      className="flex-1 text-left text-sm text-slate-700 dark:text-slate-300 hover:text-amber-600"
                    >
                      {search.name}
                    </button>
                    <button
                      onClick={() => deleteSavedSearch(search.id)}
                      className="p-1 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                {language === 'sr' ? 'Nemate sačuvanih pretraga za ovu stranicu.' : 'No saved searches for this page.'}
              </p>
            );
          })()}
        </div>
      )}

      {/* Ad Type */}
      {showAdType && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={language === 'sr' ? 'Tip oglasa' : 'Ad Type'} section="adType" />
          {expandedSections.includes('adType') && (
            <div className="flex gap-2 mt-2">
              {/* For demands page, show Kupovina/Rentiranje. For offers/auctions, show Prodaja/Izdavanje */}
              {(pageType === 'demands' ? [
                { value: 'Prodaja', label: language === 'sr' ? 'Kupovina' : 'Buying' }, 
                { value: 'Izdavanje', label: language === 'sr' ? 'Rentiranje' : 'Renting' }
              ] : [
                { value: 'Prodaja', label: language === 'sr' ? 'Prodaja' : 'Sale' }, 
                { value: 'Izdavanje', label: language === 'sr' ? 'Izdavanje' : 'Rent' }
              ]).map((type) => (
                <button
                  key={type.value}
                  onClick={() => setFilters({ adType: filters.adType === type.value ? undefined : type.value as AdType })}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filters.adType === type.value
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HITNO / Urgent Filter */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
        <label className="flex items-center gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={filters.urgentOnly || false}
            onChange={(e) => setFilters({ urgentOnly: e.target.checked || undefined })}
            className="w-5 h-5 rounded border-slate-300 dark:border-slate-600 text-red-500 focus:ring-red-500"
          />
          <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-red-500 transition-colors">
            <span className="text-red-500 font-bold">🔥</span>
            {language === 'sr' ? 'Samo HITNI oglasi' : 'Only URGENT listings'}
          </span>
        </label>
      </div>

      {/* Location */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
        <SectionHeader title={t.property.location} section="location" />
        {expandedSections.includes('location') && (
          <div className="space-y-3 mt-2">
            <MultiLocationSearch
              values={filters.locations || []}
              onChange={(locations) => {
                // Update locations and set first location for map focus
                const firstLoc = locations[0];
                setFilters({ 
                  locations: locations.length > 0 ? locations : undefined,
                  locationLat: firstLoc?.lat,
                  locationLng: firstLoc?.lng,
                  // Also set region/city for backwards compatibility with filtering
                  region: firstLoc?.type === 'region' ? firstLoc.name : firstLoc?.region,
                  city: firstLoc?.type === 'subregion' ? firstLoc.name : firstLoc?.subregion
                });
              }}
              placeholder={language === 'sr' ? 'Dodaj lokaciju...' : 'Add location...'}
              showOsmSearch={true}
              maxLocations={5}
              listings={listings}
              pageType={pageType}
            />
          </div>
        )}
      </div>

      {/* Category */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
        <SectionHeader title={t.filters.category} section="type" />
        {expandedSections.includes('type') && (
          <div className="space-y-3 mt-2">
            <select
              value={filters.category || ''}
              onChange={(e) => setFilters({ category: e.target.value as PropertyCategory || undefined, subcategory: undefined })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">{language === 'sr' ? 'Sve kategorije' : 'All categories'}</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            {filters.category && (
              <select
                value={filters.subcategory || ''}
                onChange={(e) => setFilters({ subcategory: e.target.value as PropertyCategory || undefined })}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{language === 'sr' ? 'Sve podkategorije' : 'All subcategories'}</option>
                {CATEGORIES.find(c => c.name === filters.category)?.subcategories.map((sub) => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
        <SectionHeader title={language === 'sr' ? 'Cena (EUR)' : 'Price (EUR)'} section="price" />
        {expandedSections.includes('price') && (
          <div className="flex gap-2 mt-2">
            <input
              type="number"
              min="0"
              placeholder={language === 'sr' ? 'Od' : 'From'}
              value={filters.priceMin || ''}
              onChange={(e) => setFilters({ priceMin: Number(e.target.value) || undefined })}
              className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <input
              type="number"
              min="0"
              placeholder={language === 'sr' ? 'Do' : 'To'}
              value={filters.priceMax || ''}
              onChange={(e) => setFilters({ priceMax: Number(e.target.value) || undefined })}
              className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        )}
      </div>

      {/* Price per m² / ar - Only shown on Ponude, Potražnje, Aukcije pages */}
      {showPricePerUnit && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={language === 'sr' ? 'Cena po jedinici' : 'Price per unit'} section="pricePerUnit" />
          {expandedSections.includes('pricePerUnit') && (
            <div className="space-y-3 mt-2">
              {/* Toggle between m² and ar */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setPriceUnitType('m2');
                    setFilters({ pricePerArMin: undefined, pricePerArMax: undefined });
                  }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    priceUnitType === 'm2'
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {language === 'sr' ? 'EUR/m²' : 'EUR/m²'}
                </button>
                <button
                  onClick={() => {
                    setPriceUnitType('ar');
                    setFilters({ pricePerM2Min: undefined, pricePerM2Max: undefined });
                  }}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    priceUnitType === 'ar'
                      ? 'bg-amber-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {language === 'sr' ? 'EUR/ar' : 'EUR/ar'}
                </button>
              </div>
              
              {/* Price per m² inputs */}
              {priceUnitType === 'm2' && (
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    placeholder={language === 'sr' ? 'Od EUR/m²' : 'From EUR/m²'}
                    value={filters.pricePerM2Min || ''}
                    onChange={(e) => setFilters({ pricePerM2Min: Number(e.target.value) || undefined })}
                    className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder={language === 'sr' ? 'Do EUR/m²' : 'To EUR/m²'}
                    value={filters.pricePerM2Max || ''}
                    onChange={(e) => setFilters({ pricePerM2Max: Number(e.target.value) || undefined })}
                    className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                </div>
              )}
              
              {/* Price per ar inputs */}
              {priceUnitType === 'ar' && (
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    placeholder={language === 'sr' ? 'Od EUR/ar' : 'From EUR/ar'}
                    value={filters.pricePerArMin || ''}
                    onChange={(e) => setFilters({ pricePerArMin: Number(e.target.value) || undefined })}
                    className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                  <input
                    type="number"
                    min="0"
                    placeholder={language === 'sr' ? 'Do EUR/ar' : 'To EUR/ar'}
                    value={filters.pricePerArMax || ''}
                    onChange={(e) => setFilters({ pricePerArMax: Number(e.target.value) || undefined })}
                    className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  />
                </div>
              )}
              
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {language === 'sr' 
                  ? 'Cena po m² se računa za stanove/kuće, cena po aru za placeve'
                  : 'Price per m² for apartments/houses, per ar for land plots'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Square Meters - prikazuje se samo za kategorije koje imaju kvadraturu */}
      {categoryConfig.showSquareMeters && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={language === 'sr' ? 'Kvadratura (m²)' : 'Area (m²)'} section="sqm" />
          {expandedSections.includes('sqm') && (
            <div className="flex gap-2 mt-2">
              <input
                type="number"
                placeholder={language === 'sr' ? 'Od' : 'From'}
                value={filters.squareMetersMin || ''}
                onChange={(e) => setFilters({ squareMetersMin: Number(e.target.value) || undefined })}
                className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="number"
                placeholder={language === 'sr' ? 'Do' : 'To'}
                value={filters.squareMetersMax || ''}
                onChange={(e) => setFilters({ squareMetersMax: Number(e.target.value) || undefined })}
                className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          )}
        </div>
      )}

      {/* Površina placa (ari) - prikazuje se samo za Plac kategoriju */}
      {filters.category === 'Plac' && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={language === 'sr' ? 'Površina placa (ari)' : 'Plot area (ar)'} section="plotArea" />
          {expandedSections.includes('plotArea') && (
            <div className="flex gap-2 mt-2">
              <input
                type="number"
                placeholder={language === 'sr' ? 'Od' : 'From'}
                value={filters.plotAreaMin || ''}
                onChange={(e) => setFilters({ plotAreaMin: Number(e.target.value) || undefined })}
                className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <input
                type="number"
                placeholder={language === 'sr' ? 'Do' : 'To'}
                value={filters.plotAreaMax || ''}
                onChange={(e) => setFilters({ plotAreaMax: Number(e.target.value) || undefined })}
                className="w-1/2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          )}
        </div>
      )}

      {/* Rooms - Range (od-do) - prikazuje se samo za Kuća, Stan, Poslovni prostor */}
      {categoryConfig.showRooms && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={t.property.rooms} section="rooms" />
          {expandedSections.includes('rooms') && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {/* Minimum soba (od) */}
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'sr' ? 'Od' : 'From'}
                  </label>
                  <select
                    value={filters.roomsMin ?? ''}
                    onChange={(e) => setFilters({ roomsMin: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-3 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">{language === 'sr' ? 'Bilo' : 'Any'}</option>
                    <option value="0">0 ({language === 'sr' ? 'Garsonjera' : 'Studio'})</option>
                    <option value="1">1</option>
                    <option value="1.5">1.5</option>
                    <option value="2">2</option>
                    <option value="2.5">2.5</option>
                    <option value="3">3</option>
                    <option value="3.5">3.5</option>
                    <option value="4">4</option>
                    <option value="4.5">4.5</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7+</option>
                  </select>
                </div>
                
                {/* Maksimum soba (do) */}
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'sr' ? 'Do' : 'To'}
                  </label>
                  <select
                    value={filters.roomsMax ?? ''}
                    onChange={(e) => setFilters({ roomsMax: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full px-3 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">{language === 'sr' ? 'Bilo' : 'Any'}</option>
                    <option value="0">0 ({language === 'sr' ? 'Garsonjera' : 'Studio'})</option>
                    <option value="1">1</option>
                    <option value="1.5">1.5</option>
                    <option value="2">2</option>
                    <option value="2.5">2.5</option>
                    <option value="3">3</option>
                    <option value="3.5">3.5</option>
                    <option value="4">4</option>
                    <option value="4.5">4.5</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7+ ({language === 'sr' ? 'bez limita' : 'no limit'})</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {language === 'sr' 
                  ? 'Izaberite opseg broja soba. "7+" znači 7 ili više.'
                  : 'Select room count range. "7+" means 7 or more.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Floor - prikazuje se samo za Stan i Poslovni prostor */}
      {categoryConfig.showFloor && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={language === 'sr' ? 'Sprat' : 'Floor'} section="floor" />
          {expandedSections.includes('floor') && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {/* Minimum sprat (od) */}
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'sr' ? 'Od' : 'From'}
                  </label>
                  <select
                    value={filters.floorMin ?? ''}
                    onChange={(e) => setFilters({ floorMin: e.target.value || undefined })}
                    className="w-full px-3 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">{language === 'sr' ? 'Bilo' : 'Any'}</option>
                    <option value="-3">{language === 'sr' ? '-2 ili niže' : '-2 or lower'}</option>
                    <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                    <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                    <option value="S">{language === 'sr' ? 'Suteren' : 'Semi-basement'}</option>
                    <option value="P">{language === 'sr' ? 'Prizemlje' : 'Ground floor'}</option>
                    <option value="M">{language === 'sr' ? 'Međusprat' : 'Mezzanine'}</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8+</option>
                    <option value="Pk">{language === 'sr' ? 'Potkrovlje' : 'Attic'}</option>
                    <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                    <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                  </select>
                </div>
                
                {/* Maksimum sprat (do) */}
                <div>
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'sr' ? 'Do' : 'To'}
                  </label>
                  <select
                    value={filters.floorMax ?? ''}
                    onChange={(e) => setFilters({ floorMax: e.target.value || undefined })}
                    className="w-full px-3 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">{language === 'sr' ? 'Bilo' : 'Any'}</option>
                    <option value="-3">{language === 'sr' ? '-2 ili niže' : '-2 or lower'}</option>
                    <option value="-2">-2 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                    <option value="-1">-1 ({language === 'sr' ? 'Podrum' : 'Basement'})</option>
                    <option value="S">{language === 'sr' ? 'Suteren' : 'Semi-basement'}</option>
                    <option value="P">{language === 'sr' ? 'Prizemlje' : 'Ground floor'}</option>
                    <option value="M">{language === 'sr' ? 'Međusprat' : 'Mezzanine'}</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8+</option>
                    <option value="Pk">{language === 'sr' ? 'Potkrovlje' : 'Attic'}</option>
                    <option value="Mansarda">{language === 'sr' ? 'Mansarda' : 'Mansard'}</option>
                    <option value="Tavan">{language === 'sr' ? 'Tavan' : 'Loft'}</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Heating - prikazuje se samo za Kuća i Stan */}
      {categoryConfig.showHeating && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={t.property.heating} section="heating" />
          {expandedSections.includes('heating') && (
            <div className="space-y-2 mt-2">
              {heatingOptions.map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.heating?.includes(option.value) || false}
                    onChange={() => {
                      const current = filters.heating || [];
                      const newHeating = current.includes(option.value)
                        ? current.filter(h => h !== option.value)
                        : [...current, option.value];
                      setFilters({ heating: newHeating.length ? newHeating : undefined });
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{option.label[language]}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Features (Karakteristike) - filtrirano po kategoriji */}
      {filteredFeatureOptions.length > 0 && (
        <div className="border-b border-slate-200 dark:border-slate-700 pb-4 mb-4">
          <SectionHeader title={t.property.features} section="features" />
          {expandedSections.includes('features') && (
            <div className="space-y-2 mt-2">
              {filteredFeatureOptions.map((option) => (
                <label key={option.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.features?.includes(option.key) || false}
                    onChange={() => {
                      const current = filters.features || [];
                      const newFeatures = current.includes(option.key)
                        ? current.filter(f => f !== option.key)
                        : [...current, option.key];
                      setFilters({ features: newFeatures.length ? newFeatures : undefined });
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{option.label[language]}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Only with images filter - Samo sa slikom (ispod Karakteristika) */}
      <div className="pb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.onlyWithImages || false}
            onChange={() => setFilters({ onlyWithImages: !filters.onlyWithImages })}
            className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
          />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {language === 'sr' ? 'Samo sa slikom' : 'Only with images'}
          </span>
        </label>
      </div>

      {/* Mobile close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="w-full mt-4 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
        >
          {t.filters.applyFilters}
        </button>
      )}
    </motion.div>
  );
}
